import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Search,
  Visibility,
  FilterList,
  Assignment,
  CheckCircle,
  Error,
  History,
  Store
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAppData } from '../../context/AppDataContext';
import CopyableTag from '../Shared/CopyableTag';

// Mock audit history data - similar to inventory count data structure
const mockAuditHistory = [
  {
    id: 'AUD-2024-001',
    date: '2024-01-15T10:30:00Z',
    client: 'Client A',
    location: 'Main Warehouse',
    performedBy: 'John Smith',
    status: 'completed',
    totalScanned: 125,
    totalExpected: 130,
    discrepancies: 5,
    accuracy: 96.15,
    duration: '2h 30m',
    tags: [
      { tagId: 'TAG001', sku: 'LAP-001', name: 'MacBook Pro 14"', status: 'found' },
      { tagId: 'TAG002', sku: 'MON-002', name: 'Dell Monitor 27"', status: 'found' },
      { tagId: 'TAG003', sku: 'KEY-003', name: 'Mechanical Keyboard', status: 'missing' },
      { tagId: 'TAG004', sku: 'MOU-004', name: 'Wireless Mouse', status: 'found' },
      { tagId: 'TAG005', sku: 'HDD-005', name: 'External HDD 2TB', status: 'unexpected' }
    ]
  },
  {
    id: 'AUD-2024-002',
    date: '2024-01-14T14:20:00Z',
    client: 'Client B',
    location: 'Store A',
    performedBy: 'Jane Doe',
    status: 'completed',
    totalScanned: 89,
    totalExpected: 89,
    discrepancies: 0,
    accuracy: 100.0,
    duration: '1h 45m',
    tags: [
      { tagId: 'TAG101', sku: 'TAB-001', name: 'iPad Air 5th Gen', status: 'found' },
      { tagId: 'TAG102', sku: 'PHN-001', name: 'iPhone 14 Pro', status: 'found' },
      { tagId: 'TAG103', sku: 'CAB-001', name: 'USB-C Cable', status: 'found' }
    ]
  },
  {
    id: 'AUD-2024-003',
    date: '2024-01-13T09:15:00Z',
    client: 'Client A',
    location: 'Main Warehouse',
    performedBy: 'Mike Johnson',
    status: 'in_progress',
    totalScanned: 45,
    totalExpected: 78,
    discrepancies: 2,
    accuracy: 97.56,
    duration: '1h 15m',
    tags: [
      { tagId: 'TAG201', sku: 'SPK-001', name: 'Bluetooth Speaker', status: 'found' },
      { tagId: 'TAG202', sku: 'PWR-001', name: 'Power Bank 20000mAh', status: 'missing' }
    ]
  }
];

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const getTagStatusColor = (status) => {
  switch (status) {
    case 'found':
      return 'success';
    case 'missing':
      return 'error';
    case 'unexpected':
      return 'warning';
    default:
      return 'default';
  }
};

const AuditHistory = () => {
  const { getStoreList } = useAppData();
  const [audits, setAudits] = useState(mockAuditHistory);
  const [filteredAudits, setFilteredAudits] = useState(mockAuditHistory);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter states
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);

  // Filter audits based on all filter criteria
  useEffect(() => {
    let filtered = audits;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(audit =>
        audit.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        audit.performedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        audit.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by client
    if (selectedClient) {
      filtered = filtered.filter(audit => audit.client === selectedClient);
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(audit => audit.status === selectedStatus);
    }

    // Filter by location
    if (selectedLocation) {
      filtered = filtered.filter(audit => audit.location === selectedLocation);
    }

    // Filter by date range
    if (dateFrom) {
      filtered = filtered.filter(audit => new Date(audit.date) >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(audit => new Date(audit.date) <= dateTo);
    }

    setFilteredAudits(filtered);
  }, [audits, searchTerm, selectedClient, selectedStatus, selectedLocation, dateFrom, dateTo]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (duration) => {
    return duration;
  };

  const handleViewDetail = (audit) => {
    setSelectedAudit(audit);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedAudit(null);
  };

  // Get unique clients and locations for filter dropdowns
  const uniqueClients = [...new Set(audits.map(audit => audit.client))];
  const uniqueLocations = [...new Set(audits.map(audit => audit.location))];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ px: { xs: 0, sm: 1 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 3, sm: 6 } }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: '#1e293b', 
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}>
            Inventory Audit History
          </Typography>
          <Typography variant="body1" sx={{ 
            color: '#64748b',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
            View and analyze past inventory audit results and performance metrics
          </Typography>
        </Box>

        {/* Summary Stats */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 6 } }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ py: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Assignment sx={{ color: '#8b5cf6', fontSize: { xs: 32, sm: 40 } }} />
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}>
                      {audits.length}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#64748b',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>
                      Total Audits
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ py: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircle sx={{ color: '#10b981', fontSize: { xs: 32, sm: 40 } }} />
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}>
                      {audits.filter(a => a.status === 'completed').length}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#64748b',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>
                      Completed
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ py: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <History sx={{ color: '#f59e0b', fontSize: { xs: 32, sm: 40 } }} />
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}>
                      {audits.filter(a => a.status === 'in_progress').length}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#64748b',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>
                      In Progress
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ py: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Error sx={{ color: '#ef4444', fontSize: { xs: 32, sm: 40 } }} />
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}>
                      {audits.reduce((sum, audit) => sum + audit.discrepancies, 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#64748b',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>
                      Total Discrepancies
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter Section */}
        <Card sx={{ mb: { xs: 3, sm: 6 } }}>
          <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  placeholder="Search audits..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                  >
                    <MenuItem value="">All Clients</MenuItem>
                    {uniqueClients.map((client) => (
                      <MenuItem key={client} value={client}>
                        {client}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {uniqueLocations.map((location) => (
                      <MenuItem key={location} value={location}>
                        {location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="From Date"
                  value={dateFrom}
                  onChange={setDateFrom}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="To Date"
                  value={dateTo}
                  onChange={setDateTo}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: { xs: 2, sm: 4 } }}>
            {error}
          </Alert>
        )}

        {/* Audits Table */}
        <Card>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Audit ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Accuracy</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>Scanned/Expected</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredAudits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No audit records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAudits.map((audit) => (
                    <TableRow
                      key={audit.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8fafc' },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleViewDetail(audit)}
                    >
                      <TableCell sx={{ 
                        fontWeight: 500, 
                        color: '#2563eb',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        {audit.id}
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        display: { xs: 'none', sm: 'table-cell' }
                      }}>
                        {formatDate(audit.date)}
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        display: { xs: 'none', md: 'table-cell' }
                      }}>
                        {audit.client}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={audit.status}
                          color={getStatusColor(audit.status)}
                          size="small"
                          sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        display: { xs: 'none', md: 'table-cell' },
                        color: audit.accuracy >= 95 ? '#10b981' : audit.accuracy >= 90 ? '#f59e0b' : '#ef4444',
                        fontWeight: 600
                      }}>
                        {audit.accuracy.toFixed(1)}%
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        display: { xs: 'none', lg: 'table-cell' }
                      }}>
                        {audit.totalScanned}/{audit.totalExpected}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetail(audit);
                          }}
                          sx={{ color: '#2563eb' }}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Audit Detail Dialog */}
        <Dialog open={detailOpen} onClose={handleCloseDetail} maxWidth="md" fullWidth>
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Audit Details - {selectedAudit?.id}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectedAudit && (
              <Box sx={{ mt: 1 }}>
                {/* Audit Info */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Date & Time</Typography>
                    <Typography variant="body1">{formatDate(selectedAudit.date)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Performed By</Typography>
                    <Typography variant="body1">{selectedAudit.performedBy}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Location</Typography>
                    <Typography variant="body1">{selectedAudit.location}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Duration</Typography>
                    <Typography variant="body1">{selectedAudit.duration}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Accuracy</Typography>
                    <Typography variant="body1" sx={{ 
                      color: selectedAudit.accuracy >= 95 ? '#10b981' : selectedAudit.accuracy >= 90 ? '#f59e0b' : '#ef4444',
                      fontWeight: 600
                    }}>
                      {selectedAudit.accuracy.toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Discrepancies</Typography>
                    <Typography variant="body1" sx={{ 
                      color: selectedAudit.discrepancies === 0 ? '#10b981' : '#ef4444',
                      fontWeight: 600
                    }}>
                      {selectedAudit.discrepancies}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Scanned Tags */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Scanned Items ({selectedAudit.tags.length})
                </Typography>
                <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {selectedAudit.tags.map((tag, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {tag.name}
                            </Typography>
                            <Chip
                              label={tag.status}
                              color={getTagStatusColor(tag.status)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="textSecondary">
                              SKU: {tag.sku} | Tag:
                            </Typography>
                            <CopyableTag
                              text={tag.tagId}
                              variant="text"
                              sx={{ 
                                fontSize: '0.75rem',
                                padding: '1px 4px',
                              }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetail}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AuditHistory;
